/**
 * CommandPalette — Quick Actions (Ctrl+K / Cmd+K)
 * Global search across pages, projects, clients, and vendors.
 */
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import {
  Search, Command, ArrowRight, LayoutDashboard, Briefcase, Users,
  FileText, BarChart3, UserCircle, Store, Package, Settings, Zap,
  ClipboardList, Receipt, DollarSign, Calendar, CheckSquare, ArrowUp,
  ArrowDown, CornerDownLeft, X
} from 'lucide-react';

const CATEGORY_ICONS = {
  'Navigation': LayoutDashboard,
  'Projects': Briefcase,
  'Clients': UserCircle,
  'Vendors': Store,
  'Settings': Settings,
};

const CATEGORY_ORDER = ['Navigation', 'Projects', 'Clients', 'Vendors', 'Settings'];

// Static navigation items (admin/owner/staff view)
const NAV_ITEMS = [
  { id: 'nav-dashboard', label: 'Dashboard', path: '/', category: 'Navigation', keywords: 'home overview' },
  { id: 'nav-projects', label: 'All Projects', path: '/projects', category: 'Navigation', keywords: 'project list' },
  { id: 'nav-leads', label: 'Leads', path: '/leads', category: 'Navigation', keywords: 'lead prospect pipeline' },
  { id: 'nav-ai', label: 'AI Assistant', path: '/ai-budget', category: 'Navigation', keywords: 'ai budget estimate' },
  { id: 'nav-reports', label: 'Reports', path: '/reports', category: 'Navigation', keywords: 'analytics report' },
  { id: 'nav-tasks', label: 'Tasks & Schedule', path: '/tasks', category: 'Navigation', keywords: 'task schedule timeline' },
  { id: 'nav-calendar', label: 'Calendar', path: '/calendar', category: 'Navigation', keywords: 'calendar schedule date' },
  { id: 'nav-gantt', label: 'Gantt Chart', path: '/gantt', category: 'Navigation', keywords: 'gantt timeline chart' },
  { id: 'nav-boq', label: 'BOQ Worksheets', path: '/boq-worksheets', category: 'Navigation', keywords: 'boq bill quantities worksheet' },
  { id: 'nav-invoices', label: 'Invoices', path: '/invoices', category: 'Navigation', keywords: 'invoice billing' },
  { id: 'nav-payments', label: 'Payments', path: '/payments', category: 'Navigation', keywords: 'payment transaction money' },
  { id: 'nav-activities', label: 'Vendor Activities', path: '/admin/activities', category: 'Navigation', keywords: 'activity vendor work' },
  { id: 'nav-clients', label: 'All Clients', path: '/clients', category: 'Clients', keywords: 'client customer list' },
  { id: 'nav-client-dir', label: 'Client Directory', path: '/clients-directory', category: 'Clients', keywords: 'client directory detail overview' },
  { id: 'nav-quotations-sent', label: 'Quotations Sent', path: '/client/quotations-sent', category: 'Clients', keywords: 'quotation sent proposal' },
  { id: 'nav-client-docs', label: 'Client Documents', path: '/client/documents', category: 'Clients', keywords: 'client document file' },
  { id: 'nav-client-inv', label: 'Client Invoices', path: '/client/invoices', category: 'Clients', keywords: 'client invoice' },
  { id: 'nav-client-ledger', label: 'Client Payment Ledger', path: '/client/payment-history', category: 'Clients', keywords: 'client payment ledger history' },
  { id: 'nav-vendors', label: 'All Vendors & Labour', path: '/vendors-labours', category: 'Vendors', keywords: 'vendor labour contractor list' },
  { id: 'nav-vendor-dir', label: 'Vendor Directory', path: '/vendors', category: 'Vendors', keywords: 'vendor directory detail overview' },
  { id: 'nav-vendor-quotes', label: 'Quotations Received', path: '/vendor/quotations-received', category: 'Vendors', keywords: 'quotation received vendor' },
  { id: 'nav-vendor-catalogues', label: 'Vendor Catalogues', path: '/vendor-catalogues', category: 'Vendors', keywords: 'catalogue catalog upload vendor' },
  { id: 'nav-vendor-docs', label: 'Vendor Documents', path: '/vendor/documents', category: 'Vendors', keywords: 'vendor document file' },
  { id: 'nav-vendor-rates', label: 'Vendor Rates', path: '/admin/vendor-rates', category: 'Vendors', keywords: 'rate price vendor' },
  { id: 'nav-vendor-ledger', label: 'Vendor Payment Ledger', path: '/vendor/payment-history', category: 'Vendors', keywords: 'vendor payment ledger history' },
  { id: 'nav-purchase-orders', label: 'Purchase Orders', path: '/purchase-orders', category: 'Vendors', keywords: 'purchase order po' },
  { id: 'nav-products', label: 'Products', path: '/products', category: 'Navigation', keywords: 'product item' },
  { id: 'nav-materials', label: 'Materials', path: '/materials', category: 'Navigation', keywords: 'material resource' },
  { id: 'nav-documents', label: 'Documents', path: '/documents', category: 'Navigation', keywords: 'document file' },
  { id: 'nav-users', label: 'Users', path: '/users', category: 'Navigation', keywords: 'user team member' },
  { id: 'nav-performance', label: 'Performance Dashboard', path: '/admin/performance', category: 'Navigation', keywords: 'performance api metrics speed' },
  { id: 'nav-boq-cats', label: 'BOQ Categories', path: '/settings/boq-categories', category: 'Settings', keywords: 'boq category setting' },
  { id: 'nav-pdf-templates', label: 'PDF Templates', path: '/settings/pdf-templates', category: 'Settings', keywords: 'pdf template design' },
  { id: 'nav-email-templates', label: 'Email Templates', path: '/settings/email-templates', category: 'Settings', keywords: 'email template notification' },
  { id: 'nav-notifications', label: 'Notification Preferences', path: '/settings/notifications', category: 'Settings', keywords: 'notification preference setting alert' },
];

export default function CommandPalette() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [dynamicItems, setDynamicItems] = useState([]);
  const [loadingDynamic, setLoadingDynamic] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input on open, fetch dynamic data
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
      fetchDynamicItems();
    }
  }, [open]);

  const fetchDynamicItems = useCallback(async () => {
    if (!token) return;
    setLoadingDynamic(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [projectsRes, clientsRes, vendorsRes] = await Promise.allSettled([
        axios.get(`${API}/projects`, { headers }),
        axios.get(`${API}/clients/directory`, { headers }),
        axios.get(`${API}/vendors/directory`, { headers }),
      ]);

      const items = [];
      
      if (projectsRes.status === 'fulfilled') {
        const projects = projectsRes.value.data || [];
        projects.forEach(p => {
          items.push({
            id: `proj-${p.id}`,
            label: p.name,
            sublabel: p.client_name || p.status || '',
            path: `/projects/${p.id}`,
            category: 'Projects',
            keywords: `${p.name} ${p.client_name || ''} ${p.status || ''} project`.toLowerCase(),
          });
        });
      }

      if (clientsRes.status === 'fulfilled') {
        const clients = clientsRes.value.data || [];
        clients.forEach(c => {
          items.push({
            id: `client-${c.id}`,
            label: c.name,
            sublabel: c.company_name || c.email || '',
            path: `/clients-directory/${c.id}`,
            category: 'Clients',
            keywords: `${c.name} ${c.company_name || ''} ${c.email || ''} client`.toLowerCase(),
          });
        });
      }

      if (vendorsRes.status === 'fulfilled') {
        const vendors = vendorsRes.value.data || [];
        vendors.forEach(v => {
          items.push({
            id: `vendor-${v.id}`,
            label: v.name,
            sublabel: v.company_name || v.email || '',
            path: `/vendors/${v.id}`,
            category: 'Vendors',
            keywords: `${v.name} ${v.company_name || ''} ${v.email || ''} vendor`.toLowerCase(),
          });
        });
      }

      setDynamicItems(items);
    } catch {} finally {
      setLoadingDynamic(false);
    }
  }, [token]);

  // All items combined
  const allItems = useMemo(() => [...NAV_ITEMS, ...dynamicItems], [dynamicItems]);

  // Filter by query
  const filtered = useMemo(() => {
    if (!query.trim()) return allItems.slice(0, 20); // Show top 20 when no query
    const q = query.toLowerCase().split(/\s+/);
    return allItems.filter(item => {
      const searchable = `${item.label} ${item.sublabel || ''} ${item.keywords || ''} ${item.category}`.toLowerCase();
      return q.every(word => searchable.includes(word));
    }).slice(0, 15);
  }, [query, allItems]);

  // Group by category
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(item => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    return CATEGORY_ORDER.filter(c => groups[c]).map(c => ({ category: c, items: groups[c] }));
  }, [filtered]);

  // Flat list for keyboard nav
  const flatList = useMemo(() => grouped.flatMap(g => g.items), [grouped]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, flatList.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatList[activeIndex]) {
      e.preventDefault();
      handleSelect(flatList[activeIndex]);
    }
  };

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Reset active on filter change
  useEffect(() => { setActiveIndex(0); }, [query]);

  const handleSelect = (item) => {
    setOpen(false);
    navigate(item.path);
  };

  if (!open) return null;

  let flatIdx = -1;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm"
        onClick={() => setOpen(false)}
        data-testid="command-palette-backdrop"
      />

      {/* Palette */}
      <div
        className="fixed z-[9999] left-1/2 top-[15%] -translate-x-1/2 w-full max-w-[620px] bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden"
        data-testid="command-palette"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-200">
          <Search size={18} className="text-stone-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, projects, clients, vendors..."
            className="flex-1 text-sm outline-none bg-transparent text-stone-900 placeholder:text-stone-400"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-stone-400 bg-stone-100 rounded border border-stone-200">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto py-2" data-testid="command-palette-results">
          {loadingDynamic && dynamicItems.length === 0 && (
            <div className="px-4 py-3 text-xs text-stone-400 flex items-center gap-2">
              <Zap size={12} className="animate-pulse" /> Loading projects, clients & vendors...
            </div>
          )}

          {grouped.length === 0 && (
            <div className="px-4 py-8 text-center text-stone-400">
              <Search size={24} className="mx-auto mb-2 text-stone-300" />
              <p className="text-sm">No results for "{query}"</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          )}

          {grouped.map((group) => {
            const CatIcon = CATEGORY_ICONS[group.category] || FileText;
            return (
              <div key={group.category}>
                <div className="px-4 pt-3 pb-1 flex items-center gap-1.5">
                  <CatIcon size={11} className="text-stone-400" />
                  <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider">{group.category}</span>
                </div>
                {group.items.map((item) => {
                  flatIdx++;
                  const idx = flatIdx;
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={item.id}
                      data-index={idx}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                        isActive ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'
                      }`}
                      data-testid={`command-item-${item.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : 'text-stone-800'}`}>
                          {item.label}
                        </p>
                        {item.sublabel && (
                          <p className={`text-xs truncate ${isActive ? 'text-stone-300' : 'text-stone-400'}`}>
                            {item.sublabel}
                          </p>
                        )}
                      </div>
                      {isActive && (
                        <ArrowRight size={14} className="text-stone-400 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-stone-200 bg-stone-50 flex items-center gap-4 text-[10px] text-stone-400">
          <span className="flex items-center gap-1"><ArrowUp size={10} /><ArrowDown size={10} /> Navigate</span>
          <span className="flex items-center gap-1"><CornerDownLeft size={10} /> Open</span>
          <span className="flex items-center gap-1">ESC Close</span>
          <span className="ml-auto">{flatList.length} result{flatList.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </>
  );
}
